import React, { useContext } from 'react';
import css from 'styled-jsx/css';
import { Form, Input, DatePicker, InputNumber, Select, Checkbox, Radio } from 'antd';
import DocumentInput from './DocumentInput/DocumentInput';
import CustomIcon from '../CustomIcon/CustomIcon';
import styles, { global } from './FormItem.style';
import { LanguageContext } from '../../shared/contexts/Language.context';
import { ThemeContext } from '../../shared/contexts/Theme.context';
import { isControlOrModifier, isNumber } from '../../shared/utils/keydown';
import moment from 'moment';
import DocumentCapture from './DocumentCapture/DocumentCapture';
import { normalizeText } from '../../shared/utils/utils';
import CustomPhoneInput from './CustomPhoneInput/CustomPhoneInput';
import UploadDocumentInput from './UploadDocument/UploadDocumentInput';
import CameraInput from './CameraInput/CameraInput';
import { BookingContext } from '../../shared/contexts/Booking.context';

const ADULT_MIN_YEARS_AGE = 12;
const CHILD_MAX_YEARS_AGE = 21;

const hotelSelectorKey = 'hotelId';
const nationalityKey = 'nationality';
const secondSurnameKey = 'second_surname';
const expirationDateKey = 'expiration_date';
const phoneKey = 'phone';
const photoBackKey = 'id_photo_back';
const birthDateKey = 'birth_date';
const expeditionDateKey = 'expedition_date';

export default function FormItem({
  field,
  currentValues,
  setFileValue,
  hideIcon = false,
  hideBorder = false,
  formErrors = {},
  wrapFormItem = children => <>{children}</>,
  aliceSuccessAction,
  setPhonePrefix,
  phoneNumber,
  defaultCountry,
  checkinGuestId,
  setCurrentFormValue,
  guestType,
}) {
  const {
    actions: { translate },
  } = useContext(LanguageContext);

  const { state: theme } = useContext(ThemeContext);
  const { className: themeClassName, styles: themeStyle } = styles(theme);
  const {
    state: { hotelId },
  } = useContext(BookingContext);

  const { input, rules, nested, props } = inputByType(
    field,
    currentValues,
    setFileValue,
    translate,
    wrapFormItem,
    theme.isAliceEnabled,
    aliceSuccessAction,
    setPhonePrefix,
    phoneNumber,
    defaultCountry,
    checkinGuestId,
    setCurrentFormValue,
    guestType,
    theme.isKiosk,
    hotelId,
    themeClassName
  );
  const imageDoc = isImageDocument(field.type);

  let style = `form-label`;
  if (field.type === 'document' && field.label.length > 180) {
    style += ' form-label-document';
  }

  const item = (
    <Form.Item
      label={
        field.type != 'checkbox' &&
        field.label &&
        (!theme.isAliceEnabled || !imageDoc) && (
          <span className={`${themeClassName} ${style}`}>{field.label}</span>
        )
      }
      name={field.key}
      key={field.key}
      rules={
        rules || [{ required: field.required, message: translate('guestForm.mandatoryField') }]
      }
      labelCol={{ span: 24 }}
      {...props}
      validateStatus={formErrors[field.key] && formErrors[field.key].status}
      help={formErrors[field.key] && formErrors[field.key].message}
      className={field.type.includes('radio-pre') ? 'inversed-form-item hide-required-mark' : ''}
    >
      {input}
    </Form.Item>
  );

  const existsOcrDescription = theme?.guestInfo && theme.guestInfo['ocr-desc'];
  return (
    <>
      {wrapFormItem(
        <div className={`${themeClassName} form-item-container ${hideBorder && 'no-border'}`}>
          {showIcon(field.key) && !hideIcon ? (
            <>
              <div className={`${themeClassName} icon-container ${imageDoc ? 'align-top' : ''}`}>
                <CustomIcon type={field.key}></CustomIcon>
              </div>
              <div className={`${themeClassName} item-container`}>
                {field.type === 'document_front' ? (
                  existsOcrDescription ? (
                    <p className={`${themeClassName} image-doc`}>{theme.guestInfo['ocr-desc']}</p>
                  ) : (
                    !field.required && (
                      <p className={`${themeClassName} image-doc`}>
                        {translate('guestForm.optionalDoc')}
                      </p>
                    )
                  )
                ) : (
                  ''
                )}
                {item}
              </div>
            </>
          ) : (
            <div className={`${themeClassName} no-icon-item-container`}>{item}</div>
          )}
        </div>
      )}
      {nested}
      {themeStyle}
      <style jsx global>
        {global}
      </style>
      <style jsx global>
        {`
          .inversed-form-item {
            display: flex;
            flex-direction: column-reverse;
            align-items: flex-start;
          }
          .hide-required-mark .ant-form-item-required:before {
            display: none !important;
          }
          .ant-radio-checked .ant-radio-inner {
            border-color: ${theme.mainColor} !important;
          }

          .ant-radio-checked .ant-radio-inner:after {
            background-color: ${theme.mainColor} !important;
          }

          .ant-radio:hover .ant-radio-inner {
            border-color: ${theme.mainColor} !important;
            border-color: red;
          }
        `}
      </style>
    </>
  );
}

function showIcon(key) {
  const noIcon = ['data-privacy-policy', 'data-commercial-use', 'loyalty-program'];
  return noIcon.indexOf(key) === -1;
}

function isImageDocument(type) {
  const toTop = ['document_front', 'document_back'];
  return toTop.indexOf(type) > -1;
}

function inputByType(
  field,
  currentValues,
  setFileValue,
  translate,
  wrapFormItem,
  isAliceEnabled,
  aliceSuccessAction,
  setPhonePrefix,
  phoneNumber,
  defaultCountry,
  checkinGuestId,
  setCurrentFormValue,
  guestType,
  isKiosk,
  hotelId,
  themeClassName
) {
  let nested = null;
  let input = null;
  let rules = null;
  let props = {};
  switch (field.type) {
    case 'text':
      if (field.key == phoneKey) {
        input = (
          <CustomPhoneInput
            hint={field.label}
            phoneNumber={phoneNumber}
            setPhonePrefix={setPhonePrefix}
            defaultCountry={defaultCountry}
          />
        );
      } else {
        input = <Input placeholder={field.placeholder} disabled={field.disabled} />;
      }
      break;
    case 'email':
      input = <Input placeholder={field.placeholder} />;
      rules = [
        { required: field.required, type: 'email', message: translate('guestForm.invalidEmail') },
      ];
      break;
    case 'password':
      input = <Input.Password placeholder={field.placeholder} />;
      rules = [{ required: field.required, type: 'string' }];
      break;
    case 'date':
      input = (
        <DatePicker
          getPopupContainer={trigger => trigger.parentNode}
          placeholder={'DD/MM/YYYY'}
          format={'DD/MM/YYYY'}
          disabledDate={currentDate => disabledRangeForDateInput(field.key, currentDate, guestType)}
          inputReadOnly={false}
          defaultPickerValue={getDefaultDatePickerDate(field.key, guestType)}
        />
      );
      break;
    case 'numeric':
      if (field.key == phoneKey) {
        input = (
          <CustomPhoneInput
            hint={field.label}
            phoneNumber={phoneNumber}
            setPhonePrefix={setPhonePrefix}
            defaultCountry={defaultCountry}
          />
        );
      } else {
        input = <InputNumber />;
      }

      break;
    case 'select':
      const showSearch = true; //[nationalityKey, hotelSelectorKey].indexOf(field.key) > -1;
      input = (
        <Select
          notFoundContent={translate('noData')}
          showSearch={showSearch}
          disabled={field.disabled}
          filterOption={(inputValue, option) => {
            return (
              normalizeText(option.children.toString()).indexOf(normalizeText(inputValue)) > -1
            );
          }}
          filterSort={(optionA, optionB) =>
            normalizeText(optionA.children).localeCompare(normalizeText(optionB.children))
          }
          getPopupContainer={trigger => trigger.parentElement}
        >
          {field.options
            ?.sort((option1, option2) => {
              if (option1.text < option2.text) {
                return -1;
              }
              if (option1.text > option2.text) {
                return 1;
              }
              return 0;
            })
            .map((option, idx) => {
              if (
                option.fields &&
                (option.value == currentValues[field.key] ||
                  option.text == currentValues[field.key])
              ) {
                nested = option.fields.map(field =>
                  field.key !== secondSurnameKey &&
                  field.key !== expirationDateKey &&
                  (!isAliceEnabled || field.key !== photoBackKey) ? (
                    <FormItem
                      key={field.key}
                      field={field}
                      currentValues={currentValues}
                      setFileValue={setFileValue}
                      wrapFormItem={wrapFormItem}
                      aliceSuccessAction={aliceSuccessAction}
                      checkinGuestId={checkinGuestId}
                      setCurrentFormValue={setCurrentFormValue}
                    ></FormItem>
                  ) : null
                );
              }
              return (
                <Select.Option value={option.value} key={idx}>
                  {option.text}
                </Select.Option>
              );
            })}
        </Select>
      );
      const hasNullValues = field.options ? field.options.some(option => !option.value) : true;
      if (hasNullValues) {
        rules = [];
      }
      break;
    case 'checkbox':
      input = <Checkbox>{field.label}</Checkbox>;
      props.valuePropName = 'checked';
      break;
    case 'radio':
      input = (
        <Radio.Group className="form-item-radio-group">
          {field.options?.map((option, idx) => {
            return (
              <Radio value={option.value} key={idx}>
                {option.text}
              </Radio>
            );
          })}
        </Radio.Group>
      );
      props.valuePropName = 'value';
      break;
    case 'radio-pre':
      input = (
        <Radio.Group className="form-item-radio-group">
          {field.options?.map((option, idx) => {
            return (
              <Radio value={option.value} key={idx} className={`${themeClassName}`}>
                {option.text}
              </Radio>
            );
          })}
        </Radio.Group>
      );
      props.valuePropName = 'value';
      break;
    case 'document_front':
    case 'document_back':
      if (isAliceEnabled) {
        input = (
          <DocumentCapture
            currentValues={currentValues}
            aliceSuccessAction={aliceSuccessAction}
          ></DocumentCapture>
        );
      } else {
        input = <DocumentInput field={field} setFileValue={setFileValue}></DocumentInput>;
      }
      rules = [{ required: false }];
      break;
    case 'document':
      input =
        isKiosk &&
        (hotelId == '0738' ||
          hotelId == 3918 ||
          hotelId == 'hotel-test-ih' ||
          hotelId == 3252 ||
          hotelId == '5836' ||
          hotelId == 5240 ||
          hotelId == '6167' ||
          hotelId == 5255) ? (
          <CameraInput
            field={field}
            currentValues={currentValues}
            checkinGuestId={checkinGuestId}
            setCurrentFormValue={setCurrentFormValue}
          />
        ) : (
          <UploadDocumentInput
            field={field}
            currentValues={currentValues}
            checkinGuestId={checkinGuestId}
            setCurrentFormValue={setCurrentFormValue}
          />
        );
      rules = [
        {
          required: field.required,
          message: translate('guestForm.mandatoryField'),
          validator: (_rule, _value) => {
            if (!field.required || currentValues[field.key]) return Promise.resolve();
            else return Promise.reject(translate('guestForm.mandatoryField'));
          },
        },
      ];
      break;
    default:
      input = <div>Falta implementar "{field.type}"</div>;
  }
  return { input, rules, nested, props };

  function disabledRangeForDateInput(key, currentDate, guestType) {
    const yesterday = moment().subtract(1, 'day');

    switch (key) {
      case 'entranceDate':
        return (
          !currentDate.isSame(yesterday, 'day') &&
          !currentDate?.isSameOrAfter(moment().startOf('day'))
        );
      case 'departureDate':
        const entranceDate = currentValues && currentValues['entranceDate'];
        const today = moment();
        return entranceDate
          ? !currentDate?.isSameOrAfter(entranceDate)
          : !currentDate.isSameOrAfter(today, 'day');
      case birthDateKey:
        if (guestType === 'adult' || guestType === 'holder') {
          const minDate = moment().subtract(ADULT_MIN_YEARS_AGE, 'years');
          return !currentDate.isSameOrBefore(minDate);
        } else if (guestType === 'child') {
          const maxDate = moment().subtract(CHILD_MAX_YEARS_AGE, 'years');
          return !currentDate.isSameOrAfter(maxDate);
        }
        return false;
      case expirationDateKey:
        return !currentDate?.isSameOrAfter(moment().startOf('day'));
      case expeditionDateKey:
        return currentDate?.isSameOrAfter(moment().endOf('day'));
      default:
        return false;
    }
  }

  function getDefaultDatePickerDate(key, guestType) {
    if (key === birthDateKey && guestType === 'adult') {
      return moment().subtract(ADULT_MIN_YEARS_AGE, 'years');
    } else {
      return moment();
    }
  }
}
